import type { BlueMarlinState, World } from '../types';
import { PACIFIC_BLUE_MARLIN, recruitsOf, spawningYearOf, stepBlueMarlin } from '../marlin';
import { BLUE_MARLIN_SUPPLY, BLUE_MARLIN_TABLE } from '../data/marlin/pacific';
import { STREAM, stockStream } from '../streams';
import { EXPECTED_DRAWS, stockDraws, type PopulationDraws } from '../population';
import { epochDayFromCivil, epochDayOf } from '../calendar';
import { wholeSeals } from './seals';

/** The place the blue marlin's stream is named for: their ocean, the Pacific, whose low bands they live in. */
export const BLUE_MARLIN_PLACE = 'pacific';

/** The blue marlin's draws on their stock's stream of the day (`stockDraws`, one stock): binomial deaths on each class's count, Poisson draws of the day's young. */
export function blueMarlinDraws(world: { seed: number; day: number }, streamOf: typeof stockStream = stockStream): PopulationDraws {
  return stockDraws(world, STREAM.blueMarlin, BLUE_MARLIN_PLACE, streamOf);
}

/** The young a season of spawners bears in the game: the fitted Beverton-Holt curve. */
const young = (spawnersKg: number): number => recruitsOf(PACIFIC_BLUE_MARLIN, BLUE_MARLIN_SUPPLY, spawnersKg);

/** A day of the Pacific's blue marlin (`marlin.ts`) on their own stream of the day. */
export function stepPacificBlueMarlin(world: World, streamOf: typeof stockStream = stockStream): void {
  stepBlueMarlin(PACIFIC_BLUE_MARLIN, world.migrants.blueMarlin, epochDayOf(world), BLUE_MARLIN_SUPPLY.fishing, young, blueMarlinDraws(world, streamOf));
}

/**
 * The blue marlin as a world opens on a day: the table (`data/marlin/`, the
 * stock settled on the eve of its spawning season) from the last season's
 * first day on or before it, stepped at expected values to the day, each
 * sex's classes made whole fish (`wholeSeals`; no draw: a world's opening
 * draws nothing). Every year opens alike: the stock settled at today's
 * fishing, not the assessment's past ("spawning stock biomass (SSB) estimated
 * to have fallen from approximately 133,000 mt in 1971", the ISC 2026
 * assessment, its Plenary Report, p. 30; open).
 */
export function openingBlueMarlin(epochDay: number): BlueMarlinState {
  const life = PACIFIC_BLUE_MARLIN;
  const { year } = spawningYearOf(life, epochDay);
  const w: BlueMarlinState = { females: [...BLUE_MARLIN_TABLE.females], males: [...BLUE_MARLIN_TABLE.males], spawnersKg: BLUE_MARLIN_TABLE.spawnersKg };
  for (let d = epochDayFromCivil(year, life.season.from[0], life.season.from[1]); d <= epochDay; d++) stepBlueMarlin(life, w, d, BLUE_MARLIN_SUPPLY.fishing, young, EXPECTED_DRAWS);
  return { females: wholeSeals(w.females), males: wholeSeals(w.males), spawnersKg: w.spawnersKg };
}
